import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  useTheme, 
  useMediaQuery, 
  Divider, 
  TextField,
  IconButton,
  Paper,
  CircularProgress
} from '@mui/material';
import { SpinHistory, UcPackage, PopularityCartItem, SavedTargetId, PopularityItemType } from '../types';
import { getUcPackages, getUserByTelegramId, getUserDiscounts, getSavedTargetIds, saveTargetId, deleteSavedTargetId } from '../services/api';
import UcPackagesList from '../components/packages/UcPackagesList';
import { useNavigate, useLocation } from 'react-router-dom';
import { colors } from '../theme/themeConfig';
import PageHeader from '../components/layout/PageHeader';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import StarIcon from '@mui/icons-material/Star';
import { User } from '../types';
import { getTelegramUser, openLink } from '../services/telegram';
import PaymentButton from '../components/payment/PaymentButton';
import UcIcon from '../assets/icons/UcIcon';
import { API_URL } from '../config/env';
import DiscountList from '../components/discounts/DiscountList';

const ShopPage: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [telegramUser, setTelegramUser] = useState<any>(null);
  const [packages, setPackages] = useState<UcPackage[]>([]);
  const [activeDiscounts, setActiveDiscounts] = useState<SpinHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [gameId, setGameId] = useState<string>('');
  const [gameIdVerified, setGameIdVerified] = useState<boolean>(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Saved IDs (shared for both UC and Popularity)
  const [savedIds, setSavedIds] = useState<SavedTargetId[]>([]);
  const [selectedSavedId, setSelectedSavedId] = useState<string>('');
  const [showNewIdInput, setShowNewIdInput] = useState<boolean>(false);
  const [popularityCart, setPopularityCart] = useState<PopularityCartItem[]>([]);

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 375px)');

  // Get state from location - can be UC packages or Popularity cart
  const locationState = location.state as {
    selectedPackage?: UcPackage,
    selectedPackages?: UcPackage[],
    orderType?: 'uc' | 'popularity',
    popularityCart?: PopularityCartItem[]
  } | undefined;
  const initialSelectedPackage = locationState?.selectedPackage;
  const initialSelectedPackages = locationState?.selectedPackages;
  
  useEffect(() => {
    // Initialize popularity cart from location state or localStorage
    if (locationState?.popularityCart) {
      setPopularityCart(locationState.popularityCart);
    } else {
      const savedCart = localStorage.getItem('popularityCart');
      if (savedCart) {
        try {
          setPopularityCart(JSON.parse(savedCart));
        } catch (e) {
          localStorage.removeItem('popularityCart');
        }
      }
    }
  }, []);

  useEffect(() => {
    // Initialize Telegram user and user data
    const initializeUser = async () => {
      try {
        const tgUser = getTelegramUser();
        setTelegramUser(tgUser);

        if (tgUser) {
          try {
            const userData = await getUserByTelegramId(tgUser.id);
            setUser(userData);

            // Load saved IDs for all orders (both UC and Popularity use same Game ID)
            try {
              const ids = await getSavedTargetIds();
              setSavedIds(ids);
              // Auto-select the last used (most recent) ID
              if (ids.length > 0) {
                setSelectedSavedId(ids[0].id);
                setGameIdVerified(true);
              } else {
                // If no saved IDs from API, check localStorage for session-verified ID
                const sessionId = localStorage.getItem('verifiedGameId');
                if (sessionId) {
                  setGameId(sessionId);
                  setGameIdVerified(true);
                }
              }
            } catch (idsError) {
              console.error('Error loading saved IDs:', idsError);
            }

            // Load discounts (used when UC items exist)
            try {
              const discountData = await getUserDiscounts(tgUser.id);
              setActiveDiscounts(discountData);
              console.log('Discounts loaded successfully');
            } catch (discountError) {
              console.error('Error loading discounts:', discountError);
            }
          } catch (error) {
            console.error('Error fetching user data:', error);
          }
        }
      } catch (error) {
        console.error('Error initializing user:', error);
      }
    };

    initializeUser();
  }, []);

  useEffect(() => {
    // // ==========
    // // discounts?
    // // ==========

    // const loadActiveDiscounts = async () => {
    //   try {
    //     const data = await getUserDiscounts(telegramUser?.id || 7345174833);
    //     // const data = await getUserDiscounts(telegramUser?.id || 371553525);
    //     console.log('here/laksdjlaksjdlqkwmd????')
    //     setActiveDiscounts(data);
    //   } catch (error) {
    //     console.error('Error loading active discounts:', error);
    //   } 
    // };

    // loadActiveDiscounts();

    // // ==========
    // // discounts?
    // // ==========


    // If we have selected packages with codes, we don't need to load all packages
    if (initialSelectedPackages && initialSelectedPackages.length > 0) {
      setPackages(initialSelectedPackages);
      setLoading(false);
      return;
    }

    // If we have a selected package with codes, we don't need to load all packages
    if (initialSelectedPackage && initialSelectedPackage.selectedCodes) {
      // Initialize with the selected package from the home page
      setPackages([initialSelectedPackage]);
      setLoading(false);
      return;
    }

    // Load UC packages from localStorage if not passed via location state
    const savedUcPackages = localStorage.getItem('selectedPackages');
    if (savedUcPackages) {
      try {
        const parsed = JSON.parse(savedUcPackages);
        if (parsed && parsed.length > 0) {
          setPackages(parsed);
          setLoading(false);
          return;
        }
      } catch (e) {
        localStorage.removeItem('selectedPackages');
      }
    }
    
    const loadPackages = async () => {
      try {
        setLoading(true);
        const data = await getUcPackages();
        // Initialize selectedCodes to 0 for each package
        const packagesWithSelection = data.map(pkg => ({
          ...pkg,
          selectedCodes: 0
        }));
        setPackages(packagesWithSelection);
      } catch (error) {
        console.error('Error loading packages:', error);

        // todo: upd
        // Use dummy packages if API fails
        setPackages([
          {
            id: '1',
            amount: 60,
            price: 99,
            bonusPoints: 5,
            availableCodes: 10,
            selectedCodes: 0
          },
          {
            id: '2',
            amount: 300,
            price: 499,
            bonusPoints: 25,
            availableCodes: 10,
            selectedCodes: 0
          },
          {
            id: '3',
            amount: 600,
            price: 999,
            bonusPoints: 50,
            availableCodes: 10,
            selectedCodes: 0
          },
          {
            id: '4',
            amount: 1500,
            price: 2499,
            bonusPoints: 125,
            availableCodes: 10,
            selectedCodes: 0
          }
        ]);
      } finally {
        setLoading(false);
      }
    };
    
    loadPackages();
  }, [initialSelectedPackage, initialSelectedPackages]);

  const handleBuyPackage = (pkg: UcPackage) => {
    // If the package has selected codes, proceed to checkout
    if (pkg.selectedCodes && pkg.selectedCodes > 0) {
      navigate('/checkout', { state: { selectedPackage: pkg } });
    } else {
      // Otherwise, select one code by default and update the package
      handleSelectCodes(pkg, 1);
    }
  };
  
  const handleSelectCodes = (pkg: UcPackage, count: number, shouldReset = false) => {
    // Enforce maximum limit of 10 (or availableCodes if less than 10)
    const maxAllowed = pkg.availableCodes || 0;
    const validCount = shouldReset ? count : Math.min(count, maxAllowed);
    
    setPackages(prevPackages => 
      prevPackages.map(p => 
        p.id === pkg.id ? { ...p, selectedCodes: validCount } : p
      )
    );
  };
  
  const handleGoBack = () => {
    navigate('/');
  };

  // Handler for popularity item quantity changes
  const handlePopularityQuantityChange = (itemId: string, delta: number) => {
    setPopularityCart(prevCart => {
      return prevCart.map(ci => {
        if (ci.item.id === itemId) {
          const newQty = Math.max(0, ci.quantity + delta);
          return { ...ci, quantity: newQty };
        }
        return ci;
      }).filter(ci => ci.quantity > 0);
    });
  };

  const handleAddMorePackages = () => {
    // Simply navigate back to home page without preserving selections
    navigate('/');
  };

  const handleAddMorePopularity = () => {
    navigate('/popularity');
  };

  const handleVerifyGameId = () => {
    // In a real application, this would verify the game ID with the backend
    if (gameId && gameId.startsWith('5') && gameId.length >= 9) {
      setGameIdVerified(true);
      // Save to localStorage for session persistence
      localStorage.setItem('verifiedGameId', gameId);
    } else {
      setGameIdVerified(false);
    }
  };

  const handleDeleteSavedId = async (id: string) => {
    try {
      await deleteSavedTargetId(id);
      setSavedIds(savedIds.filter(sid => sid.id !== id));
      if (selectedSavedId === id) {
        setSelectedSavedId('');
      }
    } catch (err) {
      console.error('Error deleting saved ID:', err);
    }
  };

  const handlePayment = async (method: 'card' | 'sbp') => {
    try {
      // Set processing state
      setProcessing(true);

      // Get fresh Telegram user data
      const tgUser = getTelegramUser();
      if (!tgUser) {
        setError('Не удалось получить данные пользователя');
        setProcessing(false);
        return;
      }

      // Update state for future reference
      setTelegramUser(tgUser);

      // Determine the game ID (same for both UC and Popularity)
      const finalGameId = selectedSavedId
        ? savedIds.find(sid => sid.id === selectedSavedId)?.targetId
        : gameId;

      if (!finalGameId) {
        throw new Error('Please select or enter a Game ID');
      }

      if (!gameIdVerified) {
        throw new Error('Please verify your Game ID first');
      }

      // Auto-save new ID if using new ID and under limit
      if (gameId && !selectedSavedId && savedIds.length < 5) {
        try {
          await saveTargetId(gameId);
          // Clear session storage since ID is now saved to backend
          localStorage.removeItem('verifiedGameId');
        } catch (saveError) {
          console.error('Error saving ID:', saveError);
          // Don't block the order if save fails
        }
      }

      // Determine order type dynamically
      let dynamicOrderType: string;
      if (selectedPackages.length > 0 && popularityCart.length > 0) {
        dynamicOrderType = 'combined_order';
      } else if (selectedPackages.length > 0) {
        dynamicOrderType = 'uc_order';
      } else {
        dynamicOrderType = 'popularity_order';
      }

      // Build order data with both carts (empty arrays when not applicable)
      const orderData: any = {
        telegramId: tgUser.id,
        paymentMethod: method,
        totalAmount: finalPrice,
        gameId: finalGameId,
        targetId: finalGameId,
        orderType: dynamicOrderType,
        packages: selectedPackages.map(pkg => ({
          packId: pkg.id,
          quantity: pkg.selectedCodes || 0
        })),
        popularityItems: popularityCart.map(ci => ({
          itemId: ci.item.id,
          quantity: ci.quantity
        })),
        discount: (selectedPackages.length > 0 && appliedDiscount.id) ? {
          discountId: appliedDiscount.id,
          discountAmount: appliedDiscount.amount,
          originalPrice: basePrice
        } : null
      };
      
      // Send order to backend
      const response = await fetch(`${API_URL}/payments/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.paymentUrl) {
        throw new Error('Ошибка при создании платежа.');
      }
      
      // Clear selections after order is created
      localStorage.removeItem('selectedPackages');
      localStorage.removeItem('popularityCart');

      // Open payment URL in a new tab
      openLink(data.paymentUrl);
      
      // Reset UI state
      setPackages(packages.map(pkg => ({ ...pkg, selectedCodes: 0 })));
      
      // Redirect to homepage after a short delay
      setTimeout(() => {
        navigate('/');
      }, 500);
    } catch (error) {
      console.error('Payment error:', error);
      setError('An unknown error occurred');
      
      // Show error message to user
      setTimeout(() => {
        setError(null);
      }, 2000); // Clear error after 5 seconds
    } finally {
      setProcessing(false);
    }
  };
  
  // Get all selected packages (those with selectedCodes > 0)
  const selectedPackages = packages.filter(pkg => pkg.selectedCodes && pkg.selectedCodes > 0);

  useEffect(() => {
    if (selectedPackages.length > 0) {
      localStorage.setItem('selectedPackages', JSON.stringify(selectedPackages));
    }
  }, [selectedPackages]);

  // Save popularity cart to localStorage when it changes
  useEffect(() => {
    if (popularityCart.length > 0) {
      localStorage.setItem('popularityCart', JSON.stringify(popularityCart));
    } else {
      localStorage.removeItem('popularityCart');
    }
  }, [popularityCart]);

  // Calculate totals from all selected packages (UC)
  const totalAmount = selectedPackages.reduce((sum, pkg) => sum + (pkg.amount * (pkg.selectedCodes || 0)), 0);
  const totalBonusPoints = selectedPackages.reduce((sum, pkg) => sum + (pkg.bonusPoints * (pkg.selectedCodes || 0)), 0);

  // Calculate totals for Popularity orders (matching UC format)
  const totalPP = popularityCart.reduce((sum, ci) => sum + (ci.item.ppAmount * ci.quantity), 0);
  const totalPopularityBonus = popularityCart.reduce((sum, ci) => {
    const bonus = Math.round(ci.item.price * 0.01); // ~1% bonus
    return sum + (bonus * ci.quantity);
  }, 0);

  const username = telegramUser?.username || user?.telegramUsername || 'username';
  const hasSelectedPackages = selectedPackages.length > 0 || popularityCart.length > 0;
  const hasUcItems = selectedPackages.length > 0;
  const hasPpItems = popularityCart.length > 0;

  const [appliedDiscount, setAppliedDiscount] = useState<{ id: string | null, amount: number }>({ id: null, amount: 0 });

  // Calculate base price (combined from both carts)
  const basePrice = useMemo(() => {
    const ucTotal = selectedPackages.reduce((sum, pkg) => sum + (pkg.price * (pkg.selectedCodes || 0)), 0);
    const ppTotal = popularityCart.reduce((total, ci) => total + (ci.item.price * ci.quantity), 0);
    return ucTotal + ppTotal;
  }, [selectedPackages, popularityCart]);

  // Calculate final price (after discount)
  const finalPrice = useMemo(() => {
    let price = basePrice;

    // Apply discount when UC items exist
    if (hasUcItems && appliedDiscount.id && appliedDiscount.amount > 0) {
      price = Math.max(50, price - appliedDiscount.amount);
    }

    return price;
  }, [basePrice, appliedDiscount, hasUcItems]);

  // Sort popularity cart: PP bundles first, then special items, both by ppAmount
  const sortedPopularityCart = useMemo(() => {
    return [...popularityCart].sort((a, b) => {
      // First sort by itemType (PP_BUNDLE before SPECIAL_ITEM)
      if (a.item.itemType !== b.item.itemType) {
        return a.item.itemType === PopularityItemType.PP_BUNDLE ? -1 : 1;
      }
      // Then sort by ppAmount ascending
      return a.item.ppAmount - b.item.ppAmount;
    });
  }, [popularityCart]);

  // Handler for applying discounts
  const handleApplyDiscount = useCallback((discountId: string | null, amount: number) => {
    // If no discount is being applied, clear the applied discount
    if (discountId === null) {
      setAppliedDiscount({ id: null, amount: 0 });
      console.log('Discount removed');
      return;
    }
    
    // Find the discount to check minimum purchase requirement
    const selectedDiscount = activeDiscounts.find(d => d.id === discountId);
    if (selectedDiscount && basePrice >= selectedDiscount.reward.minPurchase) {
      // Apply the discount if minimum purchase is met
      setAppliedDiscount({ id: discountId, amount });
    } else {
      // Don't apply the discount if minimum purchase is not met
      setAppliedDiscount({ id: null, amount: 0 });
    }
  }, [activeDiscounts, basePrice]); // Include activeDiscounts and basePrice in dependencies
  
  // Monitor cart total changes to auto-remove discount if minimum purchase is no longer met
  useEffect(() => {
    // If there's an applied discount, check if it still meets minimum purchase
    if (appliedDiscount.id) {
      const currentDiscount = activeDiscounts.find(d => d.id === appliedDiscount.id);
      if (currentDiscount && basePrice < currentDiscount.reward.minPurchase) {
        // Remove the discount if minimum purchase is no longer met
        setAppliedDiscount({ id: null, amount: 0 });
        console.log('Discount removed: minimum purchase no longer met');
      }
    }
  }, [basePrice, appliedDiscount.id, activeDiscounts]);
  
  return (
    <Box 
      sx={{ 
        padding: isMobile ? '16px' : '24px',
        backgroundColor: colors.background.default,
        minHeight: '100vh',
        paddingBottom: '80px' // Space for bottom navigation
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleGoBack}
          sx={{ 
            color: colors.text.primary,
            padding: 0,
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: 'transparent'
            }
          }}
        >
          Назад
        </Button>
      </Box>
      
      <PageHeader 
        username={username}
        title="Ваш заказ"
        photoUrl={telegramUser?.photoUrl}
      />
      
      {hasSelectedPackages ? (
        <Box>
          {/* Order Summary */}
          <Box
            sx={{
              backgroundColor: colors.background.card,
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px',
              border: `1px solid ${colors.primary.main}`
            }}
          >
            {/* UC Selected Packages */}
            {hasUcItems && selectedPackages.map((pkg) => (
              <Box key={pkg.id} sx={{ marginBottom: '16px' }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  {/* Column 1: UC Amount with Icon */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    flex: 1 
                  }}>
                    <Typography sx={{ 
                      fontSize: isMobile ? '18px' : '20px',
                      fontWeight: 500, 
                      color: colors.text.primary,
                      marginRight: '3px'
                    }}>
                      {pkg.amount}
                    </Typography>
                    <UcIcon 
                      width={isMobile ? 28 : 34} 
                      height={isMobile ? 28 : 34} 
                      color={colors.primary.main}
                      className="uc-icon"
                      style={{ marginLeft: isMobile ? '3px' : '6px' }}
                    />
                  </Box>
                  
                  {/* Column 2: Quantity Controls */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center',
                    flex: 1
                  }}>
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      border: `1px solid ${colors.primary.main}`,
                      borderRadius: '8px',
                      padding: '2px'
                    }}>
                      <IconButton 
                        size="small"
                        onClick={() => handleSelectCodes(pkg, Math.max(0, (pkg.selectedCodes || 0) - 1))}
                        sx={{ color: colors.primary.main }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>
                      
                      <Typography sx={{ 
                        width: '30px', 
                        textAlign: 'center',
                        fontWeight: 500
                      }}>
                        {pkg.selectedCodes}
                      </Typography>
                      
                      <IconButton 
                        size="small"
                        onClick={() => handleSelectCodes(pkg, Math.min((pkg.availableCodes || 0), (pkg.selectedCodes || 0) + 1))}
                        sx={{ color: colors.primary.main }}
                        disabled={(pkg.selectedCodes || 0) >= (pkg.availableCodes || 0)}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                  
                  {/* Column 3: Price and Bonus */}
                  <Box sx={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    flex: 1
                  }}>
                    <Typography sx={{ 
                      fontWeight: 500, 
                      color: colors.text.primary,
                      fontSize: isMobile ? '16px' : '18px'
                    }}>
                      {pkg.price * (pkg.selectedCodes || 0)} ₽
                    </Typography>
                    <Box sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px'
                    }}>
                      <Typography sx={{ 
                        fontSize: isMobile ? '11px' : '12px',
                        color: colors.secondary.main
                      }}>
                        Бонус: {pkg.bonusPoints * (pkg.selectedCodes || 0)}
                      </Typography>
                      <StarIcon sx={{ 
                        fontSize: isMobile ? '14px' : '16px', 
                        color: colors.secondary.main 
                      }} />
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}

            {/* Divider between UC and PP sections */}
            {hasUcItems && hasPpItems && (
              <Divider sx={{ margin: '16px 0' }} />
            )}

            {/* Popularity Items */}
            {hasPpItems && sortedPopularityCart.map((ci) => (
              <Box key={ci.item.id} sx={{ marginBottom: '16px' }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  {/* Column 1: PP Amount or Special Item Name */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: ci.item.itemType === PopularityItemType.SPECIAL_ITEM ? 'column' : 'row',
                    alignItems: ci.item.itemType === PopularityItemType.SPECIAL_ITEM ? 'flex-start' : 'center',
                    flex: 1
                  }}>
                    {ci.item.itemType === PopularityItemType.SPECIAL_ITEM ? (
                      <>
                        <Typography sx={{
                          fontSize: isMobile ? '16px' : '18px',
                          fontWeight: 600,
                          color: colors.text.primary,
                        }}>
                          {ci.item.name}
                        </Typography>
                        <Typography sx={{
                          fontSize: isMobile ? '12px' : '13px',
                          color: colors.text.secondary,
                        }}>
                          {ci.item.ppAmount.toLocaleString()} PP
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography sx={{
                          fontSize: isMobile ? '18px' : '20px',
                          fontWeight: 500,
                          color: colors.text.primary,
                        }}>
                          {ci.item.ppAmount.toLocaleString()}
                        </Typography>
                        <Typography sx={{
                          fontSize: isMobile ? '14px' : '16px',
                          fontWeight: 500,
                          color: colors.primary.main,
                          marginLeft: '4px'
                        }}>
                          PP
                        </Typography>
                      </>
                    )}
                  </Box>

                  {/* Column 2: Quantity Controls */}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    flex: 1
                  }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      border: `1px solid ${colors.primary.main}`,
                      borderRadius: '8px',
                      padding: '2px'
                    }}>
                      <IconButton
                        size="small"
                        onClick={() => handlePopularityQuantityChange(ci.item.id, -1)}
                        sx={{ color: colors.primary.main }}
                      >
                        <RemoveIcon fontSize="small" />
                      </IconButton>

                      <Typography sx={{
                        width: '30px',
                        textAlign: 'center',
                        fontWeight: 500
                      }}>
                        {ci.quantity}
                      </Typography>

                      <IconButton
                        size="small"
                        onClick={() => handlePopularityQuantityChange(ci.item.id, 1)}
                        sx={{ color: colors.primary.main }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Column 3: Price and Bonus */}
                  <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    flex: 1
                  }}>
                    <Typography sx={{
                      fontWeight: 500,
                      color: colors.text.primary,
                      fontSize: isMobile ? '16px' : '18px'
                    }}>
                      {ci.item.price * ci.quantity} ₽
                    </Typography>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '2px'
                    }}>
                      <Typography sx={{
                        fontSize: isMobile ? '11px' : '12px',
                        color: colors.secondary.main
                      }}>
                        Бонус: {Math.round(ci.item.price * 0.01) * ci.quantity}
                      </Typography>
                      <StarIcon sx={{
                        fontSize: isMobile ? '14px' : '16px',
                        color: colors.secondary.main
                      }} />
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}

            <Divider sx={{ margin: '12px 0' }} />

            {/* Всего UC */}
            {hasUcItems && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <Typography sx={{ color: colors.text.secondary }}>Всего UC:</Typography>
                <Typography sx={{ fontWeight: 500, color: colors.text.primary }}>
                  {totalAmount} UC
                </Typography>
              </Box>
            )}

            {/* Всего PP */}
            {hasPpItems && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <Typography sx={{ color: colors.text.secondary }}>Всего PP:</Typography>
                <Typography sx={{ fontWeight: 500, color: colors.text.primary }}>
                  {totalPP.toLocaleString()} PP
                </Typography>
              </Box>
            )}

            {/* Combined Бонусные баллы */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <Typography sx={{ color: colors.text.secondary }}>Бонусные баллы:</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Typography sx={{ fontWeight: 500, color: colors.secondary.main }}>
                  {totalBonusPoints + totalPopularityBonus}
                </Typography>
                <StarIcon sx={{
                  fontSize: isMobile ? '16px' : '18px',
                  color: colors.secondary.main
                }} />
              </Box>
            </Box>

            {/* Скидка */}
            {appliedDiscount && appliedDiscount.amount > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <Typography sx={{ color: colors.text.secondary, fontWeight: 500 }}>Скидка:</Typography>
                <Typography sx={{ fontWeight: 500, color: colors.primary.main }}>
                  -{appliedDiscount.amount} рублей
                </Typography>
              </Box>
            )}

            {/* Итого */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography sx={{ fontWeight: 600, color: colors.text.primary }}>Итого:</Typography>
              <Typography sx={{ fontWeight: 600, color: colors.text.primary }}>
                {finalPrice} рублей
              </Typography>
            </Box>
          </Box>

          {/* UC: Discounts */}
          {hasUcItems && (
            <DiscountList
              discounts={activeDiscounts}
              cartTotal={basePrice}
              onApplyDiscount={handleApplyDiscount}
              activeDiscountId={appliedDiscount.id}
            />
          )}

          {/* Game ID Input (used for both UC and Popularity) */}
          <Box sx={{ marginBottom: '24px' }}>
            <Typography
              variant="h3"
              sx={{
                fontSize: '16px',
                fontWeight: 500,
                marginBottom: '8px',
                color: colors.text.primary
              }}
            >
              Ваш ID
            </Typography>

            {/* Saved IDs as clickable chips */}
            {savedIds.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {savedIds.map(sid => (
                  <Box
                    key={sid.id}
                    onClick={() => {
                      setSelectedSavedId(sid.id);
                      setGameId('');
                      setGameIdVerified(true);
                      setShowNewIdInput(false);
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      padding: '6px 12px',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      backgroundColor: selectedSavedId === sid.id
                        ? colors.primary.main
                        : colors.background.paper,
                      border: `1px solid ${selectedSavedId === sid.id
                        ? colors.primary.main
                        : colors.border.main}`,
                      color: selectedSavedId === sid.id
                        ? '#fff'
                        : colors.text.primary,
                      transition: 'all 0.2s',
                    }}
                  >
                    <Typography sx={{ fontSize: '14px' }}>{sid.targetId}</Typography>
                    <Box
                      component="span"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSavedId(sid.id);
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 16,
                        height: 16,
                        fontSize: '12px',
                        cursor: 'pointer',
                        opacity: 0.7,
                        '&:hover': { opacity: 1 },
                      }}
                    >
                      ✖
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {/* Show input field or "Ввести другой ID" link */}
            {(savedIds.length === 0 || showNewIdInput || !selectedSavedId) ? (
              <>
                {savedIds.length > 0 && (
                  <Typography variant="subtitle2" gutterBottom>
                    Или введите новый ID:
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: '12px' }}>
                  <TextField
                    fullWidth
                    placeholder="Введите Player ID"
                    value={gameId}
                    onChange={(e) => {
                      setGameId(e.target.value);
                      setGameIdVerified(false);
                      setSelectedSavedId('');
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: colors.background.paper,
                        borderRadius: '8px',
                        '& fieldset': {
                          borderColor: colors.border.main,
                        },
                        '&:hover fieldset': {
                          borderColor: colors.primary.main,
                        },
                      },
                    }}
                  />

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleVerifyGameId}
                    disabled={!gameId.trim()}
                    sx={{
                      borderRadius: '8px',
                      whiteSpace: 'nowrap',
                      padding: '0 32px',
                      textTransform: 'none',
                      fontWeight: 500,
                    }}
                  >
                    Проверить ID
                  </Button>
                </Box>
              </>
            ) : (
              <Typography
                onClick={() => {
                  setShowNewIdInput(true);
                  setSelectedSavedId('');
                  setGameIdVerified(false);
                }}
                sx={{
                  color: colors.primary.main,
                  cursor: 'pointer',
                  fontSize: '14px',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Ввести другой ID
              </Typography>
            )}

            {gameIdVerified && (
              <Typography
                sx={{
                  color: 'green',
                  fontSize: '14px',
                  marginTop: '4px'
                }}
              >
                ID подтвержден
              </Typography>
            )}

          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', marginBottom: '12px' }}>
            <PaymentButton
              method="sbp"
              onClick={() => handlePayment('sbp')}
              disabled={processing || !gameIdVerified}
            />

            <PaymentButton
              method="card"
              onClick={() => handlePayment('card')}
              disabled={processing || !gameIdVerified}
            />
          </Box>
          
          {/* Error message display */}
          {error && (
            <Box 
              sx={{ 
                backgroundColor: 'rgba(211, 47, 47, 0.1)', 
                color: colors.error.main,
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <Typography sx={{ textAlign: 'center' }}>
                {error}
              </Typography>
            </Box>
          )}
          
          {/* Processing indicator */}
          {processing && (
            <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <CircularProgress size={24} sx={{ color: colors.primary.main }} />
              <Typography sx={{ ml: 2, color: colors.text.secondary }}>
                Processing payment...
              </Typography>
            </Box>
          )}
          
          {/* Add more packages buttons */}
          <Box sx={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handleAddMorePackages}
              sx={{
                borderRadius: '12px',
                padding: '12px',
                fontSize: '14px',
                fontWeight: 500,
                textTransform: 'none',
              }}
            >
              Добавить ещё UC
            </Button>
            <Button
              variant="outlined"
              color="primary"
              fullWidth
              onClick={handleAddMorePopularity}
              sx={{
                borderRadius: '12px',
                padding: '12px',
                fontSize: '14px',
                fontWeight: 500,
                textTransform: 'none',
              }}
            >
              Добавить ещё PP
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          <Typography 
            variant="body1" 
            sx={{ 
              fontSize: isMobile ? '14px' : '16px',
              color: colors.text.secondary,
              marginBottom: '24px'
            }}
          >
            Выберите пакет UC для покупки
          </Typography>
          
          <UcPackagesList 
            packages={packages} 
            onBuyPackage={handleBuyPackage} 
            onSelectCodes={handleSelectCodes}
            loading={loading}
          />
        </>
      )}
    </Box>
  );
};

export default ShopPage;
